import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye } from 'react-icons/fa';
import { MdEdit, MdOutlineAssessment } from 'react-icons/md';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { SearchableSelect } from './SearchableSelect';
import { FilterToggleButton } from './FilterToggleButton';
import {
  useGetActiveDepartmentsQuery,
  useGetActiveDesignationsQuery,
  useGetActiveLocationsQuery,
  useGetEmployeesQuery,
  useGetOutsourceAndProductionIndexQuery,
  useGetLocationsByDepartmentQuery,
  useUpdateAccountStatusMutation,
} from '../../redux/employeeApi';
import { accountImageDisplayUrl } from '../../utils/accountImageUrl';
import { useAuth } from '../../context/AuthContext';
import TablePagination from './TablePagination';
import { ApiLoadState, ApiLoadTableRow } from './ApiLoadState';
import { DEFAULT_PAGE_SIZE, getServerPagination } from '../../utils/pagination';
import {
  departmentNameFromAccount,
  locationNamesFromAccount,
} from '../../utils/accountRoleClassification';
import {
  EMPTY_EMPLOYEE_LIST_FILTERS,
  employeeListStateToSearchParams,
  employeesListPath,
  getInitialEmployeeListState,
} from '../../utils/employeeListState';

const EMPTY_FILTERS = EMPTY_EMPLOYEE_LIST_FILTERS;

function designationOptionLabel(d) {
  if (!d || typeof d !== 'object') return '';
  const fromRoles = Array.isArray(d.role_names)
    ? d.role_names
        .map((r) => (typeof r === 'string' ? r : r?.name))
        .filter(Boolean)
        .join(', ')
    : '';
  return (
    d.name ??
    d.designation_name ??
    d.role_name ??
    d.title ??
    fromRoles ??
    String(d.id ?? d.role_id ?? '')
  );
}

function AdminEmployeesList({ listType = 'employees' }) {
  const isOutsourceList = listType === 'outsource';
  const permissionKey = isOutsourceList ? 'outsource_employee' : 'employees';
  const { canAction, canBehaviourTrack } = useAuth();
  const canView = canAction(permissionKey, 'view');
  const canCreate = canAction(permissionKey, 'create');
  const canEdit = canAction(permissionKey, 'edit');
  const canDelete = canAction(permissionKey, 'delete');
  const employeeEditPath = (id) =>
    isOutsourceList
      ? `/dashboard/admin/outsource-employee/edit/${id}`
      : `/dashboard/admin/employees/edit/${id}`;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialListState] = useState(() =>
    isOutsourceList ? { filters: EMPTY_FILTERS } : getInitialEmployeeListState()
  );
  const [pages, setPages] = useState({ employees: 1, outsource: 1 });
  const pageKey = isOutsourceList ? 'outsource' : 'employees';
  const page = pages[pageKey];
  const setPage = (nextPage) => {
    setPages((prev) => ({
      ...prev,
      [pageKey]: typeof nextPage === 'function' ? nextPage(prev[pageKey]) : nextPage,
    }));
  };
  const [search, setSearch] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState(initialListState.filters);
  const [filterDraft, setFilterDraft] = useState(initialListState.filters);
  const listReturnState = useMemo(
    () => ({
      from: isOutsourceList
        ? '/dashboard/admin/outsource-employee'
        : employeesListPath({ filters }),
    }),
    [isOutsourceList, filters]
  );
  const [outsourceTab, setOutsourceTab] = useState('production');
  const pageSize = DEFAULT_PAGE_SIZE;
  const isProductionTab = isOutsourceList && outsourceTab === 'production';
  const canBehaviourTracking =
    (canBehaviourTrack || canAction('behaviour_tracking', 'create')) &&
    (!isOutsourceList || isProductionTab);
  const behaviourTrackingPath = (id) => `/dashboard/admin/employees/${id}/behaviour-tracking`;

  const listQuery = useMemo(() => {
    const params = { page, per_page: pageSize };
    const q = search.trim();
    if (q) params.search = q;
    if (!isOutsourceList) {
      if (filters.accountable_department_id) {
        params.accountable_department_id = filters.accountable_department_id;
      }
      if (filters.location_id) params.location_id = filters.location_id;
      if (filters.role_id) params.role_id = filters.role_id;
      if (filters.is_reporting_supervisor !== '') {
        params.is_reporting_supervisor = filters.is_reporting_supervisor === 'true';
      }
    }
    if (isOutsourceList) {
      if (outsourceTab === 'production') {
        params.employment_nature = 'gspl';
        params.cost_centre_type = 'manufacturing';
      } else {
        params.employment_nature = 'vendor';
      }
    }
    return params;
  }, [page, pageSize, search, filters, isOutsourceList, outsourceTab]);

  const employeesQuery = useGetEmployeesQuery(listQuery, {
    skip: isOutsourceList,
  });
  const outsourceQuery = useGetOutsourceAndProductionIndexQuery(listQuery, {
    skip: !isOutsourceList,
  });
  const { data: employeesRes, isFetching, isLoading, error, refetch } =
    isOutsourceList ? outsourceQuery : employeesQuery;
  const [updateAccountStatus, { isLoading: isUpdatingStatus }] =
    useUpdateAccountStatusMutation();

  const { data: departmentsRes } = useGetActiveDepartmentsQuery(undefined, {
    skip: !isFilterOpen,
  });
  const { data: designationsRes } = useGetActiveDesignationsQuery(
    filterDraft.accountable_department_id || undefined,
    { skip: !isFilterOpen }
  );
  const { data: allLocationsRes } = useGetActiveLocationsQuery(undefined, {
    skip: !isFilterOpen || Boolean(filterDraft.accountable_department_id),
  });
  const { data: locationsByDeptRes } = useGetLocationsByDepartmentQuery(
    { accountable_department_id: filterDraft.accountable_department_id },
    { skip: !isFilterOpen || !filterDraft.accountable_department_id }
  );

  const departmentOptions = useMemo(
    () =>
      (departmentsRes?.data || []).map((d) => ({
        value: String(d.id),
        label:
          d.name ??
          d.department_name ??
          d.title ??
          d.department?.name ??
          String(d.id),
      })),
    [departmentsRes]
  );

  const designationOptions = useMemo(
    () =>
      (designationsRes?.data || []).map((d) => ({
        value: String(d.id ?? d.role_id),
        label: designationOptionLabel(d),
      })),
    [designationsRes]
  );

  const locationOptions = useMemo(() => {
    const rows = filterDraft.accountable_department_id
      ? locationsByDeptRes?.data || []
      : allLocationsRes?.data || [];
    return rows.map((loc) => ({
      value: String(loc.id ?? loc.location_id),
      label: loc.name ?? loc.location_name ?? String(loc.id ?? loc.location_id),
    }));
  }, [
    filterDraft.accountable_department_id,
    locationsByDeptRes?.data,
    allLocationsRes?.data,
  ]);

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) => String(v || '').trim() !== ''),
    [filters]
  );

  useEffect(() => {
    if (isOutsourceList) return;
    const next = employeeListStateToSearchParams({ filters });
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, isOutsourceList, searchParams, setSearchParams]);

  useEffect(() => {
    if (!isFilterOpen) return;
    setFilterDraft(filters);
  }, [isFilterOpen, filters]);

  const employees = useMemo(
    () =>
      (employeesRes?.data || []).map((acc) => ({
        id: acc.id,
        empId: acc.employee_id || '',
        name: acc.name || '',
        email: acc.email || '',
        contact: acc.contact || '',
        department: departmentNameFromAccount(acc),
        designation: acc.role_name || '',
        location: locationNamesFromAccount(acc),
        manager: acc.reporting_supervisor_name || '',
        dob: acc.dob || '',
        doj: acc.date_of_joining || '',
        aadharNo: acc.aadhar_no || '',
        panNo: acc.pan_number || '',
        active: Boolean(acc.status),
        image: accountImageDisplayUrl(acc, 'employee_image') || null,
        panImage: accountImageDisplayUrl(acc, 'pan_image') || null,
      })),
    [employeesRes]
  );

  const toggleActive = (id) => {
    if (isUpdatingStatus) return;
    const target = employees.find((e) => e.id === id);
    if (!target) return;

    const nextStatus = !target.active;

    updateAccountStatus({ id, status: nextStatus })
      .unwrap()
      .then(() => toast.success('Employee status updated successfully.'))
      .catch((err) =>
        toast.error(err?.data?.message || 'Failed to update employee status.')
      );
  };

  const handleFilterDraftChange = (field) => (e) => {
    const value = e?.target?.value ?? '';
    setFilterDraft((prev) => {
      if (field === 'accountable_department_id') {
        return {
          ...prev,
          accountable_department_id: value,
          role_id: '',
          location_id: '',
        };
      }
      if (field === 'location_id') {
        return {
          ...prev,
          location_id: value,
        };
      }
      return { ...prev, [field]: value };
    });
  };

  const applyFilters = () => {
    setFilters(filterDraft);
    setPage(1);
    setIsFilterOpen(false);
  };

  const resetFilters = () => {
    setFilterDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setIsFilterOpen(false);
  };

  const { currentPage, totalCount, pageSize: effectivePageSize } = getServerPagination(
    employeesRes,
    { page, pageSize }
  );
  const pageItems = employees;
  const countReady = Boolean(employeesRes) && !error;
  const countLabel =
    search.trim() || hasActiveFilters
      ? 'Matching Employees'
      : isOutsourceList
        ? isProductionTab
          ? 'Total Production Employees'
          : 'Total Outsource Employees'
        : 'Total Employees';

  return (
    <DashboardLayout title={isOutsourceList ? 'Helper Employees' : 'Employees'}>
      <div className="space-y-4 py-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full min-w-0 md:max-w-xs">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                type="text"
                placeholder={
                  isOutsourceList
                    ? isProductionTab
                      ? 'Search Employees'
                      : 'Search Employees'
                    : 'Search Employees'
                }
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-lg border border-gray-200 bg-[#faf8f5] py-2 pl-10 pr-3 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
            {isOutsourceList ? (
              <div className="inline-flex shrink-0 flex-wrap self-start rounded-lg border border-gray-200 bg-[#faf8f5] p-1 md:self-auto">
                <button
                  type="button"
                  onClick={() => {
                    setOutsourceTab('production');
                    setPage(1);
                  }}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition sm:px-4 sm:py-2 ${
                    outsourceTab === 'production'
                      ? 'bg-brand text-white shadow-sm'
                      : 'text-gray-700 hover:bg-white'
                  }`}
                >
                  Production
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setOutsourceTab('outsource');
                    setPage(1);
                  }}
                  className={`rounded-md px-3 py-1.5 text-sm font-medium transition sm:px-4 sm:py-2 ${
                    outsourceTab === 'outsource'
                      ? 'bg-brand text-white shadow-sm'
                      : 'text-gray-700 hover:bg-white'
                  }`}
                >
                  Outsource
                </button>
              </div>
            ) : (
              <div className="flex w-full shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-end md:w-auto">
                <FilterToggleButton
                  active={hasActiveFilters}
                  onClick={() => setIsFilterOpen(true)}
                  className="w-full sm:w-auto px-4"
                  ariaLabel="Open employee filters"
                />
                {canCreate ? (
                  <Link
                    to="/dashboard/admin/employees/add"
                    state={listReturnState}
                    className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white shadow-sm sm:w-auto"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add Employee
                  </Link>
                ) : null}
              </div>
            )}
          </div>

          <ApiLoadState
            isLoading={isLoading}
            isFetching={isFetching}
            hasData={employees.length > 0}
            error={error}
            refetch={refetch}
            message="Loading employees…"
            className="py-6 md:hidden"
          />
          {!error && (employees.length > 0 || (!isLoading && !isFetching)) && pageItems.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-500 md:hidden">
              {isOutsourceList
                ? isProductionTab
                  ? 'No production employees found.'
                  : 'No outsource employees found.'
                : 'No employees found.'}
            </p>
          ) : !error && (employees.length > 0 || (!isLoading && !isFetching)) && pageItems.length > 0 ? (
            <ul className="mb-4 space-y-3 md:hidden">
              {pageItems.map((emp, index) => (
                <li
                  key={emp.id}
                  className="rounded-lg border border-gray-100 bg-[#faf8f5] p-3"
                >
                  <div className="flex items-start gap-3">
                    {emp.image ? (
                      <img
                        src={emp.image}
                        alt={emp.name}
                        className="h-10 w-10 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 shrink-0 rounded-full bg-gradient-to-br from-gray-200 to-gray-100" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-gray-900">{emp.name}</p>
                          <p className="text-xs text-gray-500">
                            ID: {emp.empId || '—'} · #{(currentPage - 1) * pageSize + index + 1}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1">
                          {canView ? (
                            <button
                              type="button"
                              onClick={() =>
                                navigate(
                                  isOutsourceList
                                    ? `/dashboard/admin/outsource-employee/${emp.id}`
                                    : `/dashboard/admin/employees/${emp.id}`,
                                  { state: listReturnState }
                                )
                              }
                              className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                              aria-label="View employee"
                            >
                              <FaEye className="h-5 w-5" />
                            </button>
                          ) : null}
                          {canEdit ? (
                            <Link
                              to={employeeEditPath(emp.id)}
                              state={listReturnState}
                              className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                              aria-label="Edit employee"
                            >
                              <MdEdit className="h-5 w-5" />
                            </Link>
                          ) : null}
                        </div>
                      </div>
                      <div className="mt-2 space-y-1 text-xs text-gray-600">
                        <p>
                          Dept: <span className="font-medium text-gray-800">{emp.department || '—'}</span>
                        </p>
                        <p>
                          Role: <span className="font-medium text-gray-800">{emp.designation || '—'}</span>
                        </p>
                        <p>
                          Supervisor:{' '}
                          <span className="font-medium text-gray-800">{emp.manager || '—'}</span>
                        </p>
                        <p>
                          Location: <span className="font-medium text-gray-800">{emp.location || '—'}</span>
                        </p>
                      </div>
                      {canDelete ? (
                        <div className="mt-3 flex items-center justify-between border-t border-gray-200/80 pt-3">
                          <span className="text-xs text-gray-500">
                            {emp.active ? 'Active' : 'Inactive'}
                          </span>
                          <div className="flex items-center gap-2">
                            {canBehaviourTracking ? (
                              <Link
                                to={behaviourTrackingPath(emp.id)}
                                state={listReturnState}
                                className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                                aria-label="Behaviour tracking"
                                title="Behaviour Tracking"
                              >
                                <MdOutlineAssessment className="h-5 w-5" />
                              </Link>
                            ) : null}
                            <button
                              type="button"
                              disabled={isUpdatingStatus}
                              onClick={() => toggleActive(emp.id)}
                              aria-label={emp.active ? 'Disable employee' : 'Enable employee'}
                              className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:cursor-not-allowed disabled:opacity-60 ${
                                emp.active
                                  ? 'border-emerald-600 bg-[#008E36]'
                                  : 'border-gray-300 bg-gray-300'
                              }`}
                            >
                              <span
                                className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                  emp.active ? 'translate-x-5' : 'translate-x-0'
                                }`}
                              />
                            </button>
                          </div>
                        </div>
                      ) : canBehaviourTracking ? (
                        <div className="mt-3 flex items-center justify-end border-t border-gray-200/80 pt-3">
                          <Link
                            to={behaviourTrackingPath(emp.id)}
                            state={listReturnState}
                            className="rounded-lg p-1.5 text-red-700 hover:bg-white"
                            aria-label="Behaviour tracking"
                            title="Behaviour Tracking"
                          >
                            <MdOutlineAssessment className="h-5 w-5" />
                          </Link>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="">
                  <th className="whitespace-nowrap border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Sr. No.
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Emp ID
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Emp Name
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                  Accountable Department
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Designation
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Reporting Supervisor
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Location
                  </th>
                  <th className="border-b border-gray-200 px-3 py-2.5 text-left font-semibold text-gray-700">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                <ApiLoadTableRow
                  colSpan={8}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  hasData={employees.length > 0}
                  error={error}
                  refetch={refetch}
                  message="Loading employees..."
                />
                {!error && (employees.length > 0 || (!isLoading && !isFetching)) &&
                  pageItems.map((emp, index) => (
                  <tr key={emp.id} className="hover:bg-amber-50/40">
                    <td className="whitespace-nowrap border-b border-gray-100 px-3 py-2.5 text-gray-600">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="border-b border-gray-100 px-3 py-2.5">{emp.empId}</td>
                    <td className="border-b border-gray-100 px-3 py-2.5">
                      <div className="flex items-center gap-3">
                        {emp.image ? (
                          <img
                            src={emp.image}
                            alt={emp.name}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-200 to-gray-100" />
                        )}
                        <span className="font-medium text-gray-800">{emp.name}</span>
                      </div>
                    </td>
                    
                    <td className="border-b border-gray-100 px-3 py-2.5">{emp.department}</td>
                    <td className="border-b border-gray-100 px-3 py-2.5">{emp.designation}</td>
                    <td className="border-b border-gray-100 px-3 py-2.5">{emp.manager || '—'}</td>
                    <td className="border-b border-gray-100 px-3 py-2.5">{emp.location}</td>
                    <td className="border-b border-gray-100 px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {canView ? (
                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                isOutsourceList
                                  ? `/dashboard/admin/outsource-employee/${emp.id}`
                                  : `/dashboard/admin/employees/${emp.id}`,
                                { state: listReturnState }
                              )
                            }
                            className="rounded-lg p-1.5 text-red-700 hover:bg-red-50"
                            aria-label="View employee"
                          >
                            <FaEye className="h-5 w-5" />
                          </button>
                        ) : null}
                        {canEdit ? (
                          <Link
                            to={employeeEditPath(emp.id)}
                            state={listReturnState}
                            className="rounded-lg p-1.5 text-red-700 hover:bg-red-50"
                            aria-label="Edit employee"
                          >
                            <MdEdit className="h-5 w-5" />
                          </Link>
                        ) : null}
                        {canBehaviourTracking ? (
                          <Link
                            to={behaviourTrackingPath(emp.id)}
                            state={listReturnState}
                            className="rounded-lg p-1.5 text-red-700 hover:bg-red-50"
                            aria-label="Behaviour tracking"
                            title="Behaviour Tracking"
                          >
                            <MdOutlineAssessment className="h-5 w-5" />
                          </Link>
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            disabled={isUpdatingStatus}
                            onClick={() => toggleActive(emp.id)}
                            aria-label={emp.active ? 'Disable employee' : 'Enable employee'}
                            className={`inline-flex h-5 w-10 items-center rounded-full border px-[2px] transition disabled:cursor-not-allowed disabled:opacity-60 ${
                              emp.active
                                ? 'border-emerald-600 bg-[#008E36]'
                                : 'border-gray-300 bg-gray-300'
                            }`}
                          >
                            <span
                              className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                                emp.active ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!error && !isLoading && !isFetching && employees.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                {isOutsourceList
                  ? isProductionTab
                    ? 'No production employees found.'
                    : 'No outsource employees found.'
                  : 'No employees found.'}
              </p>
            ) : null}
          </div>

          <div className="mt-4 grid grid-cols-1 items-center gap-2 sm:grid-cols-3">
            <p
              className="text-center text-sm text-gray-600 sm:text-left"
              aria-label={`${countLabel}: ${countReady ? totalCount : 'loading'}`}
            >
              <span className="font-medium text-gray-500">{countLabel}</span>
              {': '}
              <span className="font-semibold tabular-nums text-brand">
                {countReady ? totalCount.toLocaleString('en-IN') : '—'}
              </span>
            </p>
            <div className="flex justify-center [&>div]:mt-0">
              <TablePagination
                totalCount={totalCount}
                currentPage={currentPage}
                onPageChange={setPage}
                pageSize={effectivePageSize}
              />
            </div>
            <div className="hidden sm:block" aria-hidden />
          </div>
        </div>
      </div>

      {!isOutsourceList && isFilterOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setIsFilterOpen(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-white px-6 py-3 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-filter-title"
          >
            <div className="mb-2 flex items-center justify-between">
              <h3 id="employee-filter-title" className="text-xl font-semibold text-gray-800">
              </h3>
              <button
                type="button"
                onClick={() => setIsFilterOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-2xl leading-none text-brand hover:bg-gray-100"
                aria-label="Close filter"
              >
                ×
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="flex flex-col text-sm text-gray-700">
                Department
                <SearchableSelect
                  name="accountable_department_id"
                  value={filterDraft.accountable_department_id}
                  onChange={handleFilterDraftChange('accountable_department_id')}
                  placeholder="Select department"
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  options={departmentOptions}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Designation
                <SearchableSelect
                  name="role_id"
                  value={filterDraft.role_id}
                  onChange={handleFilterDraftChange('role_id')}
                  placeholder="Select designation"
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  options={designationOptions}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Location
                <SearchableSelect
                  name="location_id"
                  value={filterDraft.location_id}
                  onChange={handleFilterDraftChange('location_id')}
                  placeholder="Select location"
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  options={locationOptions}
                />
              </label>
              <label className="flex flex-col text-sm text-gray-700">
                Is Reporting Supervisor
                <SearchableSelect
                  name="is_reporting_supervisor"
                  value={filterDraft.is_reporting_supervisor}
                  onChange={handleFilterDraftChange('is_reporting_supervisor')}
                  placeholder="Select"
                  className="mt-1 w-full rounded-lg border border-gray-200 bg-[#faf8f5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  options={[
                    { value: 'true', label: 'Yes' },
                    { value: 'false', label: 'No' },
                  ]}
                />
              </label>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={resetFilters}
                className="min-w-[110px] rounded-lg border border-[#e8e4df] bg-white px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-[#faf8f5]"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={applyFilters}
                className="min-w-[110px] rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </DashboardLayout>
  );
}

export default AdminEmployeesList;
